import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { masterApi, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ZapCartLogo } from "@/components/ZapCartLogo";
import { AboutModal } from "@/components/AboutModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { ForgotOtpForm } from "@/components/ForgotOtpForm";
import { PasskeySignInButton } from "@/components/PasskeyManager";
import { SupportContacts } from "@/components/SupportContacts";
import { BLOCKED_MSG, isBlocked } from "@/lib/support";
import { ShieldAlert } from "lucide-react";
import { PlanPicker, usePlans } from "@/components/PlanPicker";
import { queuePasskeyOffer } from "@/lib/passkey";

export default function Auth() {
  const { loginMaster, enterPin, setActiveStaff } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | signup | forgot | member
  const [f, setF] = useState({ shop_name: "", email: "", mobile: "", password: "", address: "", postcode: "", vat_number: "", referral_code: "", plan: "trial" });
  const plans = usePlans();
  const [loading, setLoading] = useState(false);
  const [confirmPw, setConfirmPw] = useState("");
  const [blocked, setBlocked] = useState(false);
  const pwMismatch = mode === "signup" && confirmPw !== f.password;
  const [aboutOpen, setAboutOpen] = useState(false);
  const [otp, setOtp] = useState({ email_code: "", mobile_code: "" });
  const [vinfo, setVinfo] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { setF((p) => ({ ...p, referral_code: ref.toUpperCase() })); setMode("signup"); }
  }, []);

  const enterStore = async () => {
    const { data } = await api.post("/auth/enter");
    enterPin(data.token, data.user);
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await masterApi.post("/auth/verify-otp", { account_id: vinfo.account_id, email_code: otp.email_code, mobile_code: otp.mobile_code });
      loginMaster(data.token, data.account);
      await enterStore();
      queuePasskeyOffer();
      if (f.plan !== "trial") {
        try {
          const { data: co } = await masterApi.post("/payments/checkout", { origin_url: window.location.origin, plan: f.plan });
          window.location.href = co.checkout_url; return;
        } catch (e) { toast.error(`${errMsg(e)} — you're on the free trial; choose your plan later in Settings → Billing`); }
      }
      toast.success("Store verified! 🎉");
      navigate(data.account.needs_pin_setup ? "/setup-pin" : "/staff", { state: { pw: f.password } });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const resend = async () => {
    try { const { data } = await masterApi.post("/auth/resend-otp", { account_id: vinfo.account_id }); setVinfo((v) => ({ ...v, sms_required: data.sms_required })); toast.success("New code sent to your email"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const go = async () => {
    setLoading(true);
    try {
      if (mode === "signup") {
        if (pwMismatch) { toast.error("Passwords do not match"); return; }
        const { data } = await masterApi.post("/auth/register", f);
        setVinfo({ account_id: data.account_id, email: data.email, mobile: data.mobile, sms_required: data.sms_required });
        setOtp({ email_code: "", mobile_code: "" });
        setMode("otp");
        toast.success("Verification code sent — check your email");
      } else if (mode === "login") {
        const { data } = await masterApi.post("/auth/login", { email: f.email, password: f.password });
        loginMaster(data.token, data.account);
        if (data.account.must_change_password) { navigate("/force-password"); return; }
        await enterStore();
        queuePasskeyOffer();
        toast.success(`Welcome back, ${data.account.shop_name}`);
        navigate(data.account.needs_pin_setup ? "/setup-pin" : "/staff", { state: { pw: f.password } });
      } else {
        return;
      }
    } catch (e) { if (isBlocked(e)) setBlocked(true); else toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-5 relative overflow-x-clip">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm relative z-10">
        <button data-testid="auth-logo-btn" onClick={() => setAboutOpen(true)} className="w-full flex items-center justify-center gap-2 mb-1 active:scale-95 transition-transform">
          <ZapCartLogo size={48} />
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Zap<span className="text-emerald-600">Basket</span></h1>
        </button>
        <p className="text-center text-slate-500 text-sm mb-7">UK Wholesale Order & Deal Engine</p>

        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-200/60 space-y-4">
          {blocked ? (
            <div className="space-y-3" data-testid="store-blocked">
              <div className="text-center">
                <span className="w-12 h-12 mx-auto rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mb-2"><ShieldAlert className="w-6 h-6" /></span>
                <p data-testid="store-blocked-msg" className="font-bold text-slate-900">{BLOCKED_MSG}</p>
              </div>
              <SupportContacts subject="My ZapBasket store is deactivated" />
              <Button data-testid="store-blocked-back" variant="outline" onClick={() => setBlocked(false)} className="w-full h-11 font-bold">Back to login</Button>
            </div>
          ) : mode === "otp" ? (
            <div className="space-y-3" data-testid="otp-verify">
              <div className="text-center">
                <p className="font-extrabold text-slate-900">Verify your store</p>
                <p className="text-xs text-slate-500">Enter the 6-digit code sent to<br /><b>{vinfo?.email}</b>{vinfo?.sms_required && <> & <b>{vinfo?.mobile}</b> (SMS)</>}</p>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500">Email code</label>
                <Input data-testid="otp-email-code" inputMode="numeric" maxLength={6} placeholder="123456" value={otp.email_code}
                  onChange={(e) => setOtp({ ...otp, email_code: e.target.value.replace(/\D/g, "") })}
                  className="h-12 bg-slate-50 border-slate-200 tracking-[0.4em] text-center font-mono text-lg" />
              </div>
              {vinfo?.sms_required && (
              <div>
                <label className="text-xs font-bold text-slate-500">Mobile SMS code</label>
                <Input data-testid="otp-mobile-code" inputMode="numeric" maxLength={6} placeholder="123456" value={otp.mobile_code}
                  onChange={(e) => setOtp({ ...otp, mobile_code: e.target.value.replace(/\D/g, "") })}
                  className="h-12 bg-slate-50 border-slate-200 tracking-[0.4em] text-center font-mono text-lg" />
              </div>
              )}
              <Button data-testid="otp-verify-btn" disabled={loading || otp.email_code.length !== 6 || (vinfo?.sms_required && otp.mobile_code.length !== 6)} onClick={verify} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">Verify & Continue</Button>
              <div className="flex justify-between text-sm">
                <button data-testid="otp-resend" onClick={resend} className="text-blue-600 font-medium">Resend code</button>
                <button onClick={() => setMode("signup")} className="text-slate-400">← Back</button>
              </div>
            </div>
          ) : mode === "forgot" ? (
            <ForgotOtpForm initialEmail={f.email} onDone={(email) => { setF((p) => ({ ...p, email, password: "" })); setMode("login"); }} onBack={() => setMode("login")} />
          ) : (<>
          <div className="grid grid-cols-2 gap-2 mb-1">
            <button data-testid="tab-login" onClick={() => setMode("login")} className={`h-10 rounded-xl text-sm font-semibold ${mode !== "signup" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}>Store Login</button>
            <button data-testid="tab-signup" onClick={() => setMode("signup")} className={`h-10 rounded-xl text-sm font-semibold ${mode === "signup" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}>Create Store Account</button>
          </div>

          <form data-testid="auth-form" onSubmit={(e) => { e.preventDefault(); if (!loading && !pwMismatch) go(); }} className="space-y-4">
          <AnimatePresence mode="wait">
            <motion.div key={mode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {mode === "signup" && (
                <>
                  <Input data-testid="signup-shop" placeholder="Store name" value={f.shop_name} onChange={set("shop_name")} className="h-12 bg-slate-50 border-slate-200" />
                  <Input data-testid="signup-address" placeholder="Full store address" value={f.address} onChange={set("address")} className="h-12 bg-slate-50 border-slate-200" />
                  <Input data-testid="signup-postcode" placeholder="Postcode" value={f.postcode} onChange={set("postcode")} className="h-12 bg-slate-50 border-slate-200 uppercase placeholder:normal-case" />
                  <Input data-testid="signup-vat" placeholder="VAT number (optional)" value={f.vat_number} onChange={set("vat_number")} className="h-12 bg-slate-50 border-slate-200" />
                  <Input data-testid="signup-mobile" placeholder="Mobile number (optional)" value={f.mobile} onChange={set("mobile")} className="h-12 bg-slate-50 border-slate-200" />
                  <Input data-testid="signup-referral" placeholder="Referral code (optional) — get 10 extra free days" value={f.referral_code} onChange={set("referral_code")} className="h-12 bg-emerald-50 border-emerald-200 uppercase placeholder:normal-case" />
                </>
              )}
              <Input data-testid="auth-email" type="email" name="email" autoComplete="username" placeholder="Store email" value={f.email} onChange={set("email")} className="h-12 bg-slate-50 border-slate-200" />
              {mode !== "forgot" && (
                <PasswordInput data-testid="auth-password" name="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} placeholder="Password" value={f.password} onChange={set("password")} className="h-12 bg-slate-50 border-slate-200" />
              )}
              {mode === "signup" && (
                <>
                  <div data-testid="signup-plan" className="space-y-1.5">
                    <p className="text-xs font-bold text-slate-500">Choose your plan</p>
                    <PlanPicker plans={plans} value={f.plan} onChange={(plan) => setF((p) => ({ ...p, plan }))} includeTrial />
                    {f.plan !== "trial" && <p data-testid="signup-plan-note" className="text-[11px] text-slate-500">After verifying your email you'll add a card. You get the 30-day free trial first, and your first charge is when it ends.</p>}
                  </div>
                  <PasswordInput data-testid="signup-confirm-password" autoComplete="new-password" placeholder="Confirm password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="h-12 bg-slate-50 border-slate-200" />
                  {confirmPw && pwMismatch && <p data-testid="signup-password-mismatch" className="text-xs font-semibold text-red-600">Passwords do not match</p>}
                </>
              )}
            </motion.div>
          </AnimatePresence>

          <Button type="submit" data-testid="auth-submit" disabled={loading || pwMismatch} className="w-full h-12 bg-[#1E3A8A] hover:bg-[#152a63] text-white font-bold">
            {mode === "signup" ? "Create Account" : "Log In"}
          </Button>
          </form>
          {mode === "login" && (
            <PasskeySignInButton app="store" auto={!new URLSearchParams(window.location.search).get("ref")} onBlocked={() => setBlocked(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onDone={(d) => {
              loginMaster(d.master_token, d.account);
              if (d.account.must_change_password) { navigate("/force-password"); return; }
              if (d.account.is_platform_admin) { enterPin(d.token, d.user); setActiveStaff({ name: "Manager", color: "#1E3A8A" }); navigate("/admin/stores"); return; }
              if (d.token && d.staff) {
                enterPin(d.token, d.user); setActiveStaff({ name: d.staff.name, color: d.staff.color });
                toast.success(`Signed in as ${d.staff.name}`);
                const back = sessionStorage.getItem("zb_return_to"); sessionStorage.removeItem("zb_return_to");
                navigate(back || "/dashboard"); return;
              }
              navigate(d.account.needs_pin_setup ? "/setup-pin" : "/staff");
            }} />
          )}

          {mode !== "forgot" ? (
            <button data-testid="link-forgot" onClick={() => setMode("forgot")} className="w-full text-center text-slate-400 hover:text-slate-600 text-sm">Forgot / Reset Password?</button>
          ) : (
            <button onClick={() => setMode("login")} className="w-full text-center text-slate-400 hover:text-slate-600 text-sm">← Back to login</button>
          )}
          <button data-testid="link-about" onClick={() => setAboutOpen(true)} className="w-full text-center text-slate-500 hover:text-slate-700 text-sm font-medium">
            ℹ️ About ZapBasket & How It Works (Demo)
          </button>
          </>)}
        </div>
      </motion.div>
      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}
